import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Brand, Spinner } from "../components/Brand";
import { useSEO } from "../lib/seo";

const EMPTY = {
    name: "",
    email: "",
    mobile: "",
    company: "",
    service: "",
    subject: "",
    message: "",
    website: "", // honeypot
};

export default function Contact() {
    useSEO({
        title: "Contact — Luxurio Edge Technical Services",
        description:
            "Get in touch with Luxurio Edge for premium on-demand home services. Send us your enquiry and our team will respond within one business day.",
        canonical: "https://luxurioedgetechnical.com/contact",
    });

    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [services, setServices] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/enquiry/services");
                setServices(data.items || []);
            } catch (_) { /* fallback to free text */ }
        })();
    }, []);

    const set = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Please enter your full name";
        if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email";
        if (!/^[+0-9\s().-]{6,24}$/.test(form.mobile.trim()))
            e.mobile = "Please enter a valid mobile number";
        if (form.subject.trim().length < 3) e.subject = "Subject is too short";
        if (form.message.trim().length < 10)
            e.message = "Please provide a bit more detail (10+ characters)";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setBusy(true);
        try {
            await api.post("/enquiry", form);
            setDone(true);
            setForm(EMPTY);
            toast.success("Thank you — we'll be in touch within one business day.");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Could not submit. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const inputBase =
        "w-full bg-transparent border-b border-white/20 px-1 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition";

    return (
        <div className="min-h-screen bg-[#0F0614] text-white">
            {/* Top bar */}
            <header className="px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between border-b border-[#2B1438]">
                <Link to="/" data-testid="contact-brand-link">
                    <Brand light size="small" />
                </Link>
                <nav className="flex items-center gap-3">
                    <Link
                        to="/"
                        data-testid="contact-home-link"
                        className="text-sm text-white/70 hover:text-[#D4AF37] transition px-3 py-2"
                    >
                        Home
                    </Link>
                    <Link
                        to="/login"
                        data-testid="contact-login-link"
                        className="text-sm text-white/70 hover:text-[#D4AF37] transition px-3 py-2"
                    >
                        Sign in
                    </Link>
                    <Link
                        to="/register"
                        data-testid="contact-cta-book"
                        className="lx-btn-gold px-5 py-2.5 text-sm"
                    >
                        Book a Service
                    </Link>
                </nav>
            </header>

            <main className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20 grid lg:grid-cols-12 gap-16">
                {/* Left: copy / context */}
                <aside className="lg:col-span-5">
                    <div className="lx-label text-[#D4AF37] mb-4">Reach Out</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
                        Let&apos;s talk about <span className="italic lx-gold-text">your needs.</span>
                    </h1>
                    <p className="text-white/60 leading-relaxed mb-10 max-w-md">
                        Whether you&apos;re planning a one-off service, an annual maintenance
                        contract, or a partnership — drop us a note. Our concierge team
                        responds within one business day.
                    </p>

                    <div className="space-y-6 max-w-md">
                        <ContactBlock
                            label="Email"
                            value="hello@luxurioedgetechnical.com"
                            href="mailto:hello@luxurioedgetechnical.com"
                        />
                        <ContactBlock
                            label="Phone / WhatsApp"
                            value="+971 56 496 4949"
                            href="tel:+971564964949"
                        />
                        <ContactBlock
                            label="Headquarters"
                            value="Dubai · Abu Dhabi · Sharjah"
                        />
                    </div>
                </aside>

                {/* Right: form */}
                <section className="lg:col-span-7" data-testid="enquiry-form-section">
                    {done ? (
                        <div className="lx-card-dark p-10 text-center" data-testid="enquiry-success">
                            <div className="lx-label text-[#D4AF37] mb-4">Message received</div>
                            <h2 className="font-serif text-3xl mb-4">Thank you.</h2>
                            <p className="text-white/60 max-w-md mx-auto mb-8">
                                We&apos;ve logged your enquiry and dispatched an acknowledgment to your inbox.
                                One of our advisors will be in touch within one business day.
                            </p>
                            <button
                                data-testid="enquiry-submit-another"
                                onClick={() => setDone(false)}
                                className="lx-btn-gold px-6 py-3 text-sm"
                            >
                                Submit another enquiry
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-7" noValidate>
                            {/* Honeypot — visually hidden but accessible to bots */}
                            <div
                                style={{
                                    position: "absolute",
                                    left: "-10000px",
                                    width: 1,
                                    height: 1,
                                    overflow: "hidden",
                                }}
                                aria-hidden="true"
                            >
                                <label>
                                    Website
                                    <input
                                        type="text"
                                        name="website"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={form.website}
                                        onChange={set("website")}
                                    />
                                </label>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-7">
                                <Field label="Full Name *" error={errors.name}>
                                    <input
                                        data-testid="enq-name"
                                        type="text"
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Jane Doe"
                                        className={inputBase}
                                        autoComplete="name"
                                    />
                                </Field>
                                <Field label="Email *" error={errors.email}>
                                    <input
                                        data-testid="enq-email"
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="jane@example.com"
                                        className={inputBase}
                                        autoComplete="email"
                                    />
                                </Field>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-7">
                                <Field label="Mobile *" error={errors.mobile}>
                                    <input
                                        data-testid="enq-mobile"
                                        type="tel"
                                        value={form.mobile}
                                        onChange={set("mobile")}
                                        placeholder="+971 50 123 4567"
                                        className={inputBase}
                                        autoComplete="tel"
                                    />
                                </Field>
                                <Field label="Company">
                                    <input
                                        data-testid="enq-company"
                                        type="text"
                                        value={form.company}
                                        onChange={set("company")}
                                        placeholder="Optional"
                                        className={inputBase}
                                        autoComplete="organization"
                                    />
                                </Field>
                            </div>

                            <Field label="Service of interest">
                                <select
                                    data-testid="enq-service"
                                    value={form.service}
                                    onChange={set("service")}
                                    className={`${inputBase} appearance-none`}
                                    style={{ colorScheme: "dark" }}
                                >
                                    <option value="" className="bg-[#180A21]">
                                        Select a service (optional)
                                    </option>
                                    {services.map((s) => (
                                        <option key={s} value={s} className="bg-[#180A21]">
                                            {s}
                                        </option>
                                    ))}
                                    <option value="Other" className="bg-[#180A21]">Other / Not sure</option>
                                </select>
                            </Field>

                            <Field label="Subject *" error={errors.subject}>
                                <input
                                    data-testid="enq-subject"
                                    type="text"
                                    value={form.subject}
                                    onChange={set("subject")}
                                    placeholder="How can we help?"
                                    className={inputBase}
                                />
                            </Field>

                            <Field label="Message *" error={errors.message}>
                                <textarea
                                    data-testid="enq-message"
                                    rows={5}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Tell us a little about what you need…"
                                    className={`${inputBase} resize-none`}
                                />
                            </Field>

                            <div className="flex items-center justify-between pt-2">
                                <p className="text-xs text-white/40">
                                    By submitting you agree to be contacted about your enquiry.
                                </p>
                                <button
                                    type="submit"
                                    data-testid="enq-submit"
                                    disabled={busy}
                                    className="lx-btn-gold px-8 py-3.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
                                >
                                    {busy ? <Spinner /> : "Send Enquiry →"}
                                </button>
                            </div>
                        </form>
                    )}
                </section>
            </main>

            <footer className="border-t border-[#2B1438] py-8 text-center text-xs text-white/40 tracking-wider">
                © {new Date().getFullYear()} Luxurio Edge Technical Services LLC.
            </footer>
        </div>
    );
}

function Field({ label, error, children }) {
    return (
        <label className="block">
            <div className="lx-label text-white/50 mb-2">{label}</div>
            {children}
            {error && (
                <div className="text-xs text-rose-400 mt-2" data-testid="field-error">
                    {error}
                </div>
            )}
        </label>
    );
}

function ContactBlock({ label, value, href }) {
    const inner = (
        <>
            <div className="lx-label text-[#D4AF37] mb-1">{label}</div>
            <div className="text-white/80 text-base">{value}</div>
        </>
    );
    return href ? (
        <a href={href} className="block group hover:translate-x-1 transition-transform">
            {inner}
        </a>
    ) : (
        <div>{inner}</div>
    );
}
